import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import * as _ from '../lodash';
import Alert from '../components/Alert';
import Button from '../components/Common/Button';
import DatePicker from '../components/DatePicker';
import ExceededMessage from "../components/exceeded_free_trial_message.js";
import LocationSelect from './components/LocationSelect';
import Price from './components/Price';
import QuantityInput from './components/QuantityNumberInput';
import Select from '../components/Common/Select';
import TimeZoneSelect from '../components/Common/TimeZoneSelect';
import TimeZone from '../components/Common/TimeZone';
import TimeSlotRadioButtons from './components/TimeSlotRadioButtons';
import Translation from '../utilities/localizationUtility';
import VariantSelect from './components/VariantSelect';
import { formatHours, formatPrice, getTimeFrameFromDate, convertDateFormat } from '../utilities/timeUtility';
import { stringToBoolean } from '../utilities/conversionUtility';
import { SHORT_DATE_FORMAT } from '../constants';
import {
  capitalize,
  customSelectStyles,
  parseInt,
} from '../utilities/helperUtility';

const firstSelectableDate = (frames, availableDates) => {
  const dates = frames.map(x => x.date);
  const today = new Date();
  let firstBookableDate = _.first(_.filter(dates, x => moment(x).diff(today, 'days') >= 0));
  if (firstBookableDate) {
    if (!availableDates.includes(firstBookableDate)) {
      firstBookableDate = availableDates[0];
    }
    return firstBookableDate;
  }
  return null;
};

const parseAvailableDates = (frames, totalQuantity, minDate, maxDate, timeZone, locationId) => {
  const today = moment.tz(timeZone);
  return frames.reduce((arr, frame) => {
    const theDate = moment.tz(frame.date, timeZone);
    let isValid = false;
    if (theDate.isSameOrAfter(today.clone().startOf('day'))) {
      const frameLocationIds = frame.locationIds || [];
      if (!locationId || frame.timeslots.some(x => !x.location_id || x.location_id == locationId)) isValid = true;
    }
    if (isValid && _.find(frame.timeslots, x => moment.tz(x.start, timeZone).isSameOrAfter(today))) {
      arr.push(frame.date);
    }
    return arr;
  }, []);
};

export default class ClassPage extends Component {
  state = {
    currentMonth: null,
    availableDates: [],
    width: window.innerWidth,
  };

  updateDimensions = this.updateDimensions.bind(this);

  ref = React.createRef();

  UNSAFE_componentWillReceiveProps(newProps) {
    const {
      frames,
      capacityType,
      totalQuantity,
      selectedDate,
      selectedLocation,
      selectedVariant,
      selectedTimeslot,
      setting,
      locale,
      timeZone,
      localTimeZone,
      maxAvailability,
      minDate,
      maxDate,
      orderActions: { setOrderDetails },
      frameActions: { fetchFrames, changeFramesTimezone },
      settingActions: { setLocalTimeZone },
      exceededTrialPlan
    } = newProps;

    const { currentMonth } = this.state,
          currentSelectedLocation = this.props.selectedLocation || {},
          nextSelectedLocation = newProps.selectedLocation || {},
          currentSelectedVariant = this.props.selectedVariant || {},
          nextSelectedVariant = newProps.selectedVariant || {};

    const { localTimeZone: prevLocalTimeZone, setting: prevSetting } = this.props;
    const prevShowTZChooser = stringToBoolean(prevSetting.show_timezone_chooser);
    const showTZChozer = stringToBoolean(setting.show_timezone_chooser);

    if (!_.isEqual(nextSelectedLocation, currentSelectedLocation) || !_.isEqual(nextSelectedVariant, currentSelectedVariant)) {
      if (!exceededTrialPlan && currentMonth) {
        const { start, finish } = getTimeFrameFromDate(currentMonth, localTimeZone);
        fetchFrames({ start: start.format(), finish: finish.format(), location_id: nextSelectedLocation.id});
      }
      setOrderDetails({ date: null, selectedTimeslot: null });
    }

    if (!_.isEqual(prevLocalTimeZone, localTimeZone)) {
      changeFramesTimezone(localTimeZone);
      if(!!selectedTimeslot) {
        setOrderDetails({
          date: moment.tz(selectedTimeslot.start, localTimeZone).format(SHORT_DATE_FORMAT)
        })
      }
    }

    if (!showTZChozer && !_.isEqual(prevShowTZChooser, showTZChozer)) {
      setLocalTimeZone(timeZone);
    }

    if (frames.length > 0 && selectedVariant) {
      const availableDates = parseAvailableDates(frames, totalQuantity, minDate, maxDate, localTimeZone, nextSelectedLocation.id);

      if (!selectedDate && !currentMonth) {
        const firstBookableDate = firstSelectableDate(frames, availableDates);
        if (firstBookableDate) {
          setOrderDetails({ date: firstBookableDate });
          const today = moment().format('YYYY-MM'),
                cm = moment(firstBookableDate).format('YYYY-MM');
          if (today === cm) {
            // if this month is same with first bookable date's month, doesn't load frames.
            this.setState({currentMonth: moment(firstBookableDate).format(SHORT_DATE_FORMAT)});
          } else {
            // if this month is different with first bookable date's month, go to first bookable date's month.
            this.onMonthChange(firstBookableDate);
          }
        }
      } else {
        this.setState({ availableDates });
      }
    } else {
      this.setState({ availableDates: [] });
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  updateDimensions() {
    this.setState({ width: window.innerWidth });
  }

  onDayClick(day, modifiers = {}) {
    if (modifiers.disabled) return;
    const {
      orderActions: { setOrderDetails },
      selectedDate,
    } = this.props;

    const newSelection = moment(day).format(SHORT_DATE_FORMAT);

    if (newSelection !== selectedDate) {
      setOrderDetails({
        date: newSelection,
        selectedTimeslot: null,
      });
    }
  }

  dayDisabled(value) {
    const day = moment(value).format(SHORT_DATE_FORMAT),
          { availableDates } = this.state;

    if (!availableDates.includes(day)) return new Date(day);

    return null;
  }

  fakeDisabled(value) {
    const { frames } = this.props,
          day = moment(value).format(SHORT_DATE_FORMAT);

    if (frames.some(frame => frame.date === day)) return new Date(day);

    return null;
  }

  onSelectTimeSlot(timeslot) {
    const {
      orderActions: { setOrderDetails },
      capacityType,
      frames,
      maxAvailability,
      selectedDate,
      selectedVariant
    } = this.props;

    if (timeslot) {
      const frame = frames.find(x => x.date === selectedDate);
      let available = maxAvailability - _.sumBy(timeslot.availability, 'bookings')

      if (capacityType == 1) {
        const variantAvailability = timeslot.availability.find(v => v.external_id == selectedVariant);
        available = variantAvailability ? variantAvailability.available : 0;
      } else if (capacityType == 2) {
        available = timeslot.resource_availability;
      }

      setOrderDetails({
        date: frame.date,
        available: available,
        selectedTimeslot: timeslot,
      });
    }
  }

  onSubmit(e) {
    const {
      selectedDate,
      selectedTimeslot,
      totalQuantity,
      orderActions,
      cartActions,
      additionalDetails,
      setting
    } = this.props;
    const ele = e.target,
          messages = [];

    if (!selectedDate) {
      messages.push('PLEASE SELECT A DATE');
    } else if (!selectedTimeslot) {
      messages.push('PLEASE SELECT A TIME');
    }

    if (!messages.length) {
      ele.disabled = true;
      orderActions
        .submitOrder()
        .then((response) => {
          const { reservation } = response.data;
          if (reservation.items.some(x => x.status !== 'ok')) {
            let m = 'SCHEDULE CONFLICT PLEASE RELOAD PAGE';

            if (window.screen.width < 380) m = 'PLEASE RELOAD PAGE';
            this.alert.show('red', [m]);
            ele.innerText = m;
          } else {
            cartActions.addItems(reservation, ['Date', 'Time', 'Location']);
          }
        })
        .catch((responsed) => {
          ele.disabled = false;
          const { response } = responsed;
          if (response && response.data) {
            const { data } = response;

            Object.keys(data).forEach((key) => {
              const name = key
                .split('_')
                .map(x => capitalize(x))
                .join('');
              messages.push(`${name} ${data[key]}`);
            });

            this.alert.show('red', ['SERVER ERROR']);
          } else {
            this.alert.show('red', ['SOMETHING WRONG']);
            throw responsed;
          }
        });
    } else {
      this.alert.show('red', messages);
    }
  }

  onMonthChange(value) {
    const {
      frameActions: { fetchFrames },
      exceededTrialPlan,
    } = this.props;

    const selectedLocation = this.props.selectedLocation || {};

    this.setState(
      {
        currentMonth: moment(value).format(SHORT_DATE_FORMAT),
      },
      () => {
        if (!exceededTrialPlan) {
          const { start, finish } = getTimeFrameFromDate(moment(value).format(SHORT_DATE_FORMAT), this.timeZone());
          fetchFrames({ start: start.format(), finish: finish.format(), location_id: selectedLocation.id });
        }
      },
    );
  }

  goNextMonthButton() {
    const { nextOccurrence } = this.props;
    if (nextOccurrence) this.setState({currentMonth: moment(nextOccurrence).format(SHORT_DATE_FORMAT)});
  }

  renderTimeslots() {
    const { selectedDate } = this.props,
          t = new Translation(this.props),
          timeSlots = this.renderTimeSlot(selectedDate);

    if (timeSlots.props.timeslots.length) {
      const result = (
        <>
          <p className="text-left" style={{margin: '0 0 10px 0'}}>{t.key('availableSlotsTitle')}</p>
          <div className="time-slot-radio-buttons">
            <div className="text-left" ref={this.ref}>
              {timeSlots}
            </div>
          </div>
        </>
      );
      const { ref } = this;
      setTimeout(() => {
        // trick to trigger react transition
        // we can not use visibility here due to it need to overwrite plugin
        if (ref.current) {
          const current = ref.current,
                style = current.style,
                offsetHeight = current.offsetHeight,
                height = 110;

          style.maxHeight = height + 'px';
          style.borderBottom = [offsetHeight, offsetHeight - 1].includes(height) ? '1px solid #d8dfe1' : '';
        }
      }, 1);
      return result;
    }

    return null;
  }

  getAvailability(timeslot) {
    const { maxAvailability, selectedVariant, capacityType } = this.props;
    let availability = maxAvailability - _.sumBy(timeslot.availability, 'bookings')

    if (capacityType == 1) {
      const variantAvailability = timeslot.availability.find(v => v.external_id == selectedVariant);
      availability = variantAvailability ? variantAvailability.available : 0;
    } else if (capacityType == 2) {
      availability = timeslot.resource_availability;
    }

    return availability
  }

  renderTimeSlot(value) {
    const {
      frames,
      setting,
      style,
      theme,
      locale,
      totalQuantity,
      selectedTimeslot,
      selectedLocation,
      orderActions: { setOrderDetails },
    } = this.props;

    const { availableDates, timeSlotSelected } = this.state,
          locationId = selectedLocation ? selectedLocation.id : null,
          timeFormat = setting.time_format,
          t = new Translation(this.props);

    let frame = null,
        timeslots = [];

    if (value) {
      frame =  _.find(frames, x => x.date === value);

      if (frame) {
        timeslots = _.filter(frame.timeslots, x => moment(x.start).isSameOrAfter(moment()) && (!locationId || !x.location_id || x.location_id == locationId))
                        .sort((x, y) => moment(y.start).isSameOrAfter(moment(x.start)) ? -1 : 1);
      } else {
        setOrderDetails({ date: null, selectedTimeslot: null });
      }
    }

    return (
      <TimeSlotRadioButtons
        timeslots={timeslots}
        timeZone={this.timeZone()}
        locale={locale}
        isDisplayRange={true}
        isShowRemainingSpace={stringToBoolean(setting.show_remaining_places)}
        availableDates={availableDates}
        timeFormat={timeFormat}
        selectedTimeSlot={selectedTimeslot}
        getAvailability={this.getAvailability.bind(this)}
        selectedDate={value}
        onItemClick={this.onSelectTimeSlot.bind(this)}
        quantity={totalQuantity}
        style={style}
        translation={t}
      />
    );
  }

  timeZone() {
    const { setting, localTimeZone, timeZone } = this.props;
    return stringToBoolean(setting.show_timezone_chooser) ? localTimeZone : timeZone;
  }

  render() {
    const {
      lastOccurrence,
      nextOccurrence,
      totalPrice,
      frames,
      variants,
      locations,
      region,
      selectedDate,
      selectedTimeslot,
      selectedVariant,
      totalQuantity,
      available,
      orderVariants,
      translation,
      style,
      setting,
      exceededTrialPlan,
      orderActions
    } = this.props;

    const { currentMonth, width } = this.state,
          firstOccurrence = _.min(frames.map(x => moment(x.date))),
          selectedDateObj = selectedDate ? moment(selectedDate).toDate() : null,
          showLocationChooser = stringToBoolean(setting.show_location_chooser),
          showTimezoneChooser = stringToBoolean(setting.show_timezone_chooser),
          showQuantityChooser = stringToBoolean(setting.show_quantity_chooser),
          enableLocationDropdown = showLocationChooser && locations.length > 0,
          enableVariantDropdown = variants.length > 1,
          additionalStyle = {...style.body, ...{textAlign: 'center', navButtonOffset: '15px'}};

    let showGoNextMonthButton = false;

    if (currentMonth) {
      const date = moment(currentMonth);
      showGoNextMonthButton = nextOccurrence
        && frames.filter(x => moment(x.date).format('YYYY-MM') === date.format('YYYY-MM')).length === 0
        && date.isBefore(moment(nextOccurrence));
    }

    const canBook = available >= totalQuantity && frames.length && variants.length && selectedVariant,
          t = new Translation(this.props),
          submitText = canBook ? t.key('submit') : t.key('noAvailability'),
          defaultWidth = '450px',
          isLargeScreen = width > 800,
          wrapperStyle = { ...style.body, minHeight: '420px' };

    if (!isLargeScreen) wrapperStyle.maxWidth = defaultWidth;

    let toMonth = null;
    if (frames.length) {
      if (moment(currentMonth).isAfter(moment(lastOccurrence))) {
        toMonth = moment(currentMonth).toDate();
      } else {
        toMonth = moment(lastOccurrence).toDate()
      }
    }

    return (
      <div className="text-center center" style={wrapperStyle}>
        <div className="sub-panel">
          <p className="text-left" style={{ ...style.title, marginTop: '0' }}>
            {t.key('title')}
          </p>

          {exceededTrialPlan &&
            <div className="text-left">
              <Alert unclosed={true} messages={[<ExceededMessage/>]} type="exceeded-trial-plan" />
            </div>
          }

          {enableLocationDropdown &&
            <div className='text-left mt10'>
              <span className='titles' style={{...style.body}}>{t.key('locationPrompt')}</span>
              <LocationSelect className="mt10" />
            </div>
          }

          {enableVariantDropdown &&
            <div className='text-left mt10'>
              <span className='titles' style={{...style.body}}> {t.key('variantPrompt')} </span>
              <VariantSelect priceSymbol={t.key('priceSymbol')} className="mt10" />
            </div>
          }

          <div className="relative date-picker-container">
            <p className="text-left">{t.key('subTitle')}</p>

            <DatePicker
              fixedWeeks
              styled={{...style.datepicker, ...additionalStyle}}
              region={region}
              className={isLargeScreen ? 'DayPicker--moved-left' : ''}
              fromMonth={moment(firstOccurrence).toDate()}
              toMonth={toMonth}
              month={moment(currentMonth || new Date()).toDate()}
              onMonthChange={this.onMonthChange.bind(this)}
              onDayClick={this.onDayClick.bind(this)}
              modifiers={{
                selected: selectedDateObj,
                disabled: this.dayDisabled.bind(this),
                fakeDisabled: this.fakeDisabled.bind(this),
              }}
            />
          </div>

          <div className={isLargeScreen ? 'Select--moved-right' : ''}>
            {showGoNextMonthButton ? (
              <Button
                className="go-new-month"
                reverse
                styled={style.submit}
                style={{
                  fontSize: '14px',
                  paddingTop: '9px',
                  paddingBottom: '9px',
                }}
                onClick={this.goNextMonthButton.bind(this)}
              >
                {t.key('goNextMonth')}
              </Button>
            ) : (
              selectedDate && this.renderTimeslots()
            )}
          </div>

          {showQuantityChooser && <QuantityInput label={t.key('quantityPrompt')} />}

          <div className='text-left mt15'>
            {showTimezoneChooser ? (
              <React.Fragment>
                <div className='titles' style={{...style.body, ...{float: 'left', padding: '10px 10px 10px 0'}}}>{t.key('timezone')}</div>
                <TimeZoneSelect className="text-left" />
              </React.Fragment>
            ) : (
              <TimeZone timeZone={this.timeZone()} selectedTime={selectedTimeslot ? selectedTimeslot.start : null}/>
            )}
            
          </div>

          <Price style={{...style.body}} title={`${t.key('total')}`}/>

          <Alert ref={ref => (this.alert = ref)} />

          <Button
            styled={style.submit}
            disabled={!canBook}
            className="submit ptb15"
            onClick={e => this.onSubmit(e)}>
              <i className="fa fa-shopping-cart" aria-hidden="true" style={{marginRight: '10px'}}></i>
              {submitText}
          </Button>
        </div>
      </div>
    )
  }
}
